import React, { useCallback, useEffect, useRef, useState } from "react";
import api, { errMsg, downloadFile } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FileText, Upload, Download, Loader2, CheckCircle2, Clock, Files } from "lucide-react";
import { fmtDate } from "../../pages/external/referralConstants";

const STATUS_TONE = {
  requested: "text-[#B7791F] bg-[#B7791F]/12", uploaded: "text-[#2563EB] bg-[#2563EB]/12",
  under_review: "text-[#2C6480] bg-[#2C6480]/12", needs_clarification: "text-[#9C1E22] bg-[#9C1E22]/12",
  approved: "text-[#2F7D5A] bg-[#2F7D5A]/12", archived: "text-[#6B7280] bg-[#6B7280]/12",
};

const UPLOAD_TYPES = [
  ["quote", "Quote"], ["estimate", "Estimate"], ["proposal", "Proposal"], ["scope_of_work", "Scope of Work"],
  ["facility_info", "Facility / Provider Info"], ["tour_packet", "Tour Packet"],
  ["professional_review", "Professional Review"], ["requested_document", "Requested Document"], ["other", "Other"],
];

export default function ExternalRefDocuments({ referralId }) {
  const [docs, setDocs] = useState([]);
  const [canUpload, setCanUpload] = useState(true);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("quote");
  const [title, setTitle] = useState("");
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/external/referrals/${referralId}/documents`);
      setDocs(data.documents || []); setCanUpload(data.can_upload);
    } catch { /* silent */ } finally { setLoading(false); }
  }, [referralId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("doc_type", docType);
      if (title) fd.append("title", title);
      const { data } = await api.post(`/external/referrals/${referralId}/documents/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.auto_advanced ? "Uploaded — referral moved to Quote / Estimate Uploaded." : "Document uploaded.");
      setTitle(""); if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch (e) { toast.error(errMsg(e)); } finally { setUploading(false); }
  };

  const dl = async (d) => {
    try { await downloadFile(`/external/documents/${d.id}/download`, d.filename || d.title); }
    catch (e) { toast.error(errMsg(e, "Download failed.")); }
  };

  return (
    <Card className="p-5 border-[#E6EDF2]" data-testid="external-referral-documents">
      <div className="text-[13px] font-semibold text-[#252221] mb-3 flex items-center gap-1.5"><Files size={15} /> Documents</div>

      {canUpload && (
        <div className="rounded-lg border border-dashed border-[#1C4358]/30 bg-[#1C4358]/4 p-3 mb-3">
          <div className="grid sm:grid-cols-[160px_1fr] gap-2">
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger className="h-9 text-[13px]" data-testid="external-doc-type"><SelectValue /></SelectTrigger>
              <SelectContent>{UPLOAD_TYPES.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" className="h-9 text-[14px]" data-testid="external-doc-title" />
          </div>
          <input ref={fileRef} type="file" className="hidden" data-testid="external-doc-file" onChange={(e) => upload(e.target.files?.[0])} />
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="mt-2 bg-[#2F7D5A] hover:bg-[#276849] text-white gap-1.5 rounded-full h-10" data-testid="external-doc-upload-btn">
            {uploading ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} Upload quote / estimate / document
          </Button>
        </div>
      )}

      {loading ? <div className="text-[13px] text-muted-foreground">Loading…</div> :
        docs.length === 0 ? <div className="text-[13px] text-muted-foreground">No documents yet.</div> : (
          <div className="space-y-2">
            {docs.map((d) => (
              <div key={d.id} className="flex items-center gap-3 rounded-lg border border-[#E6EDF2] p-2.5" data-testid={`external-doc-${d.id}`}>
                <span className="h-8 w-8 rounded-lg bg-[#1C4358]/8 text-[#1C4358] flex items-center justify-center shrink-0"><FileText size={15} /></span>
                <div className="min-w-0 flex-1">
                  <div className="text-[13px] font-medium text-[#252221] truncate">{d.title}</div>
                  <div className="text-[11px] text-[#9AA5B1] flex items-center gap-1.5">
                    {d.doc_type_label} · {d.uploaded_by} · {fmtDate(d.updated_at)}
                    {d.response && <span className="text-[#2C6480]">· reply from MMM</span>}
                  </div>
                  {d.response && <div className="text-[12px] text-[#4B5563] mt-1 bg-[#F1F5F8] rounded px-2 py-1">{d.response}</div>}
                </div>
                <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${STATUS_TONE[d.status] || STATUS_TONE.uploaded}`}>{d.status_label}</span>
                {d.has_file && <button onClick={() => dl(d)} className="text-[#1C4358] hover:text-[#9C1E22]" data-testid={`external-doc-download-${d.id}`}><Download size={16} /></button>}
              </div>
            ))}
          </div>
        )}
    </Card>
  );
}
